import { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"

import { ScheduleDTO } from "../../../../../models/ScheduleDTO"
import { cursoCorto } from "../../../../../utils/cursoCorto"
import { horarioBonito } from "../../../../../utils/horarioBonito"

import BotonAbrirAula, { tieneAula } from "../abrirAulaBoton"
import BotonPortalClase from "../portalClaseBoton"
import "./tarjetas.css"
import { ahoraCdmx } from "../../../../../utils/horaMexico"

/*
	Vista de telefono del modulo de asistencias.

	La tabla de escritorio tiene ocho columnas y en un telefono se vuelve una
	rejilla que hay que arrastrar de lado. Aqui cada horario es una tarjeta con
	lo que sirve para escoger: curso, horario y periodo. Los datos de gestion
	(profesor, codigo, aula, estatus, vigencia) solo los ve el personal
	administrativo; al profesor no hay que decirle quien es el profesor.

	Decisiones de Esteban (2026-09-16): entrada por tarjetas y no directo a la
	clase de hoy, porque tienen varios horarios del mismo curso; fuera "Meses a
	facturar"; fuera las horas de sesion calculadas.
*/

// El backend manda las sesiones como "Sá 16:00-19:30, Do 12:00-15:30": dos
// letras del dia y las horas. Las horas traen errores conocidos, asi que de
// aqui solo se usa el DIA, para saber si al horario le toca hoy.
const DIAS_CORTOS = ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sá"]

const leTocaHoy = (sesiones : string | null | undefined) : boolean =>
{
	if(!sesiones) return false

	const hoy = DIAS_CORTOS[ahoraCdmx().getDay()]

	return String(sesiones)
		.split(",")
		.some(trozo => trozo.trim().startsWith(hoy))
}

const primeraPalabra = (texto : any) : string =>
	String(texto || "").trim().split(/\s+/)[0] || ""

const nombreDeProfesor = (horario : ScheduleDTO) : string =>
	[primeraPalabra(horario.mentorFirstName), primeraPalabra(horario.mentorLastName)]
		.filter(Boolean)
		.join(" ") || String(horario.mentor || "").trim()

type Props =
{
	schedules  : ScheduleDTO[]
	esMentor   : boolean
	onSchedule : (horario : ScheduleDTO) => void
	idleLocked : boolean
}

const Tarjeta = ({horario, esMentor, onSchedule, idleLocked, pasada} :
	{horario : ScheduleDTO, esMentor : boolean, onSchedule : (h : ScheduleDTO) => void,
	 idleLocked : boolean, pasada : boolean}) =>
{
	const hoy      = !pasada && leTocaHoy(horario.sessions)
	const profesor = nombreDeProfesor(horario)

	return <div className={"waTarjeta" + (pasada || !hoy ? " apagada" : "")}>
		<div className="waTarjetaTitulo">
			<span className="waCurso">
				{cursoCorto(horario.course)}
			</span>
			{
				hoy
					? <span className="waChip waChipHoy">Hoy</span>
					: (pasada ? <span className="waChip waChipPasada">{horario.status}</span> : null)
			}
		</div>
		<span className="waHorario">
			{horarioBonito(horario.name)}
		</span>
		<span className="waPeriodo">
			Periodo {horario.period}
		</span>
		{
			esMentor ? null : <dl className="waTarjetaMeta">
				{
					!profesor ? null : <>
						<dt>Profesor</dt><dd>{profesor}</dd>
					</>
				}
				<dt>Código</dt><dd>{horario.code}</dd>
				<dt>Aula</dt><dd>{[horario.building, horario.classroom].filter(Boolean).join(" · ")}</dd>
				<dt>Estatus</dt><dd>{horario.status}</dd>
				<dt>Vigencia</dt><dd>{[horario.startDate, horario.endDate].filter(Boolean).join(" – ")}</dd>
			</dl>
		}
		{
			hoy && tieneAula(horario) && esMentor
				? <BotonAbrirAula horario={horario} className="waTarjetaAccion" />
				: null
		}
		<button
			type="button"
			className={"waTarjetaAccion" + (pasada || !hoy || (esMentor && tieneAula(horario)) ? " secundaria" : "")}
			disabled={idleLocked}
			onClick={() => onSchedule(horario)}
		>
			{pasada ? "Ver asistencias" : (hoy ? "Pasar lista" : "Ver horario")}
		</button>
		{
			esMentor && !pasada
				? <BotonPortalClase horario={horario} className="waTarjetaAccion secundaria" />
				: null
		}
	</div>
}

const TarjetasHorarios = ({schedules, esMentor, onSchedule, idleLocked} : Props) =>
{
	const [anterioresAbierto, setAnterioresAbierto] = useState<boolean>(false)

	const {vigentes, anteriores} = useMemo
	(
		() =>
		{
			const limpios = (schedules || []).filter(Boolean)
			// "Activo" es el unico estatus que sigue corriendo; el resto ya paso.
			const vivos   = limpios.filter(h => h.status === "Activo")
			const viejos  = limpios.filter(h => h.status !== "Activo")

			// Las de hoy arriba; el resto conserva el orden que trae el servidor.
			const hoyPrimero = vivos
				.map((h, i) => ({h : h, i : i, hoy : leTocaHoy(h.sessions)}))
				.sort((a, b) => (a.hoy === b.hoy ? a.i - b.i : (a.hoy ? -1 : 1)))
				.map(x => x.h)

			return {vigentes : hoyPrimero, anteriores : viejos}
		},
		[schedules]
	)

	return <div className="waTarjetas">
		{
			vigentes.length === 0
				?
				<p className="waVacio">
					No hay horarios que coincidan.
				</p>
				:
				<div className="waListaTarjetas">
					{
						vigentes.map(horario => <Tarjeta
							key={"H_" + horario.scheduleId}
							horario={horario}
							esMentor={esMentor}
							onSchedule={onSchedule}
							idleLocked={idleLocked}
							pasada={false}
						/>)
					}
				</div>
		}

		{
			anteriores.length === 0 ? null : <div className="waAnteriores">
				<button
					type="button"
					className="waAnterioresTitulo"
					onClick={() => setAnterioresAbierto(!anterioresAbierto)}
					aria-expanded={anterioresAbierto}
				>
					<span>
						Clases anteriores
					</span>
					<span className="waAnterioresCuenta">
						{anteriores.length}
						&nbsp;
						<FontAwesomeIcon icon={anterioresAbierto ? solid("chevron-up") : solid("chevron-down")} />
					</span>
				</button>
				{
					!anterioresAbierto ? null : <div className="waListaTarjetas">
						{
							anteriores.map(horario => <Tarjeta
								key={"P_" + horario.scheduleId}
								horario={horario}
								esMentor={esMentor}
								onSchedule={onSchedule}
								idleLocked={idleLocked}
								pasada={true}
							/>)
						}
					</div>
				}
			</div>
		}
	</div>
}

export default TarjetasHorarios
